/**
 * NotificationService - FCM Push Notification Integration
 *
 * Handles push notification registration, token management, and notification
 * handling for iOS and Android platforms using Capacitor Push Notifications.
 *
 * @see WI-033 - FCM Integration
 * @see notification.interface.ts for type definitions
 */

import { Injectable, inject, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { devLog, devError, devWarn } from '@core';
import { FirestoreService } from './firestore.service';
import { AuthService } from './auth.service';
import {
  INotificationService,
  PermissionStatus,
  ReceivedNotification,
  NotificationActionEvent
} from './notification.interface';

@Injectable({ providedIn: 'root' })
export class NotificationService implements INotificationService, OnDestroy {
  private readonly firestore = inject(FirestoreService);
  private readonly authService = inject(AuthService);

  // Subscription management
  private readonly subscriptions = new Subscription();
  private listenerInitialized = false;

  // Token management
  private currentToken: string | null = null;
  private readonly tokenSubject = new BehaviorSubject<string | null>(null);

  // Event subjects
  private readonly tokenRefreshSubject = new Subject<string>();
  private readonly notificationReceivedSubject = new Subject<ReceivedNotification>();
  private readonly notificationActionSubject = new Subject<NotificationActionEvent>();

  constructor() {
    devLog('[NotificationService] Constructed');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.tokenRefreshSubject.complete();
    this.notificationReceivedSubject.complete();
    this.notificationActionSubject.complete();
    devLog('[NotificationService] Destroyed');
  }

  /**
   * Check if running on native platform (iOS/Android)
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get current platform name
   */
  getPlatform(): 'ios' | 'android' | 'web' {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      return platform;
    }
    return 'web';
  }

  /**
   * Check current notification permission status
   */
  async checkPermissions(): Promise<PermissionStatus> {
    if (!this.isNativePlatform()) {
      devWarn('[NotificationService] checkPermissions: Not on native platform');
      return 'denied';
    }

    try {
      const result = await PushNotifications.checkPermissions();
      return this.mapPermissionStatus(result.receive);
    } catch (error) {
      devError('[NotificationService] checkPermissions failed:', error);
      return 'denied';
    }
  }

  /**
   * Request notification permissions from user
   */
  async requestPermissions(): Promise<PermissionStatus> {
    if (!this.isNativePlatform()) {
      devWarn('[NotificationService] requestPermissions: Not on native platform');
      return 'denied';
    }

    try {
      const result = await PushNotifications.requestPermissions();
      const status = this.mapPermissionStatus(result.receive);
      devLog('[NotificationService] Permission result:', status);
      return status;
    } catch (error) {
      devError('[NotificationService] requestPermissions failed:', error);
      return 'denied';
    }
  }

  /**
   * Register for push notifications and get device token
   */
  async register(): Promise<string> {
    if (!this.isNativePlatform()) {
      throw new Error('Push notifications only available on native platforms');
    }

    // Initialize listeners if not already done
    if (!this.listenerInitialized) {
      await this.initializeListeners();
    }

    // Check permissions first
    const permission = await this.checkPermissions();
    if (permission !== 'granted') {
      const requested = await this.requestPermissions();
      if (requested !== 'granted') {
        throw new Error('Push notification permission not granted');
      }
    }

    // Register with FCM
    await PushNotifications.register();

    // Wait for token (with timeout)
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('FCM registration timeout'));
      }, 30000);

      const tokenSub = this.tokenSubject.subscribe(token => {
        if (token) {
          clearTimeout(timeout);
          tokenSub.unsubscribe();
          resolve(token);
        }
      });

      // If we already have a token, resolve immediately
      if (this.currentToken) {
        clearTimeout(timeout);
        tokenSub.unsubscribe();
        resolve(this.currentToken);
      }
    });
  }

  /**
   * Get current device token (if registered)
   */
  async getToken(): Promise<string | null> {
    return this.currentToken;
  }

  /**
   * Listen for token refresh events
   */
  onTokenRefresh(): Observable<string> {
    return this.tokenRefreshSubject.asObservable();
  }

  /**
   * Listen for received notifications
   */
  onNotificationReceived(): Observable<ReceivedNotification> {
    return this.notificationReceivedSubject.asObservable();
  }

  /**
   * Listen for notification action taps
   */
  onNotificationAction(): Observable<NotificationActionEvent> {
    return this.notificationActionSubject.asObservable();
  }

  /**
   * Unregister from push notifications
   */
  async unregister(): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    try {
      await PushNotifications.removeAllListeners();
      this.currentToken = null;
      this.tokenSubject.next(null);
      this.listenerInitialized = false;
      devLog('[NotificationService] Unregistered from push notifications');
    } catch (error) {
      devError('[NotificationService] unregister failed:', error);
    }
  }

  /**
   * Initialize and save FCM token for authenticated user
   * Call this after user is authenticated
   */
  async initializeForUser(): Promise<void> {
    if (!this.isNativePlatform()) {
      devLog('[NotificationService] Skipping FCM init - not on native platform');
      return;
    }

    try {
      const token = await this.register();
      await this.saveTokenToFirestore(token);
      devLog('[NotificationService] FCM initialized for user');
    } catch (error) {
      devError('[NotificationService] initializeForUser failed:', error);
      // Don't throw - FCM failure shouldn't block app usage
    }
  }

  /**
   * Initialize Capacitor Push Notification listeners
   */
  private async initializeListeners(): Promise<void> {
    if (this.listenerInitialized) {
      return;
    }

    devLog('[NotificationService] Initializing listeners');

    // Registration success
    await PushNotifications.addListener('registration', (token: Token) => {
      devLog('[NotificationService] Registration successful, token received');
      this.handleTokenReceived(token.value);
    });

    // Registration error
    await PushNotifications.addListener('registrationError', (error: { error: string }) => {
      devError('[NotificationService] Registration failed:', error.error);
    });

    // Notification received (foreground)
    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        devLog('[NotificationService] Notification received (foreground)');
        this.handleNotificationReceived(notification, true);
      }
    );

    // Notification action performed (user tapped)
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        devLog('[NotificationService] Notification action performed');
        this.handleNotificationAction(action);
      }
    );

    this.listenerInitialized = true;
  }

  /**
   * Handle received FCM token
   */
  private handleTokenReceived(token: string): void {
    const isNewToken = this.currentToken !== token;
    this.currentToken = token;
    this.tokenSubject.next(token);

    if (isNewToken) {
      this.tokenRefreshSubject.next(token);
      // Save to Firestore if user is authenticated
      this.saveTokenToFirestore(token).catch(error => {
        devError('[NotificationService] Failed to save token:', error);
      });
    }
  }

  /**
   * Save FCM token to user profile in Firestore
   */
  private async saveTokenToFirestore(token: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      devWarn('[NotificationService] Cannot save token - no authenticated user');
      return;
    }

    try {
      await this.firestore.updateDoc(`users/${user.uid}`, {
        fcmToken: token,
        fcmTokenUpdatedAt: this.firestore.fieldValue.serverTimestamp(),
        platform: this.getPlatform()
      });
      devLog('[NotificationService] Token saved to Firestore');
    } catch (error) {
      devError('[NotificationService] Failed to save token to Firestore:', error);
      throw error;
    }
  }

  /**
   * Handle received push notification
   */
  private handleNotificationReceived(
    notification: PushNotificationSchema,
    foreground: boolean
  ): void {
    const received: ReceivedNotification = {
      id: notification.id || crypto.randomUUID(),
      payload: {
        title: notification.title || '',
        body: notification.body || '',
        data: notification.data as Record<string, string> | undefined
      },
      foreground,
      receivedAt: new Date()
    };

    this.notificationReceivedSubject.next(received);
  }

  /**
   * Handle notification action (user tapped notification or action button)
   */
  private handleNotificationAction(action: ActionPerformed): void {
    const notification: ReceivedNotification = {
      id: action.notification.id || crypto.randomUUID(),
      payload: {
        title: action.notification.title || '',
        body: action.notification.body || '',
        data: action.notification.data as Record<string, string> | undefined
      },
      foreground: false,
      receivedAt: new Date()
    };

    const event: NotificationActionEvent = {
      notification,
      actionId: action.actionId
    };

    this.notificationActionSubject.next(event);
  }

  /**
   * Map Capacitor permission result to our PermissionStatus
   */
  private mapPermissionStatus(status: string): PermissionStatus {
    switch (status) {
      case 'granted':
        return 'granted';
      case 'denied':
        return 'denied';
      default:
        return 'prompt';
    }
  }
}
